<template>
  <div data-rukh-app-root class="min-h-screen bg-surface-muted">
    <ImpersonationBanner
      v-if="authStore.isImpersonating"
      :impersonated-user-name="impersonatedTargetName"
      :stopping="isStoppingImpersonation"
      @stop="handleStopImpersonation"
    />
    <AppLayoutWithSidebar
      v-if="useSidebarLayout"
      :router-view-key="routerViewKey"
    />
    <RouterView v-else :key="routerViewKey" />
    <BaseToastContainer ref="toastContainer" />
    <DesignJobTimerToast />
    <TimezoneSelectionModal
      v-model="showTimezoneModal"
      @saved="handleTimezoneSaved"
    />
    <div
      v-if="showUpdateBanner"
      class="fixed bottom-0 inset-x-0 z-[100] flex justify-center px-4 pb-4 pointer-events-none"
      role="status"
    >
      <div
        class="pointer-events-auto flex flex-wrap items-center justify-center gap-3 rounded-lg border border-border-default bg-surface px-4 py-3 shadow-lg max-w-lg"
      >
        <span class="text-sm text-text">A new version of the app is available.</span>
        <button
          type="button"
          class="text-sm font-medium text-primary hover:underline"
          @click="reloadAppForUpdate"
        >
          Reload now
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick, provide, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppLayoutWithSidebar from '@/components/layout/AppLayoutWithSidebar.vue'
import ImpersonationBanner from '@/components/auth/ImpersonationBanner.vue'
import BaseToastContainer from '@/components/feedback/BaseToastContainer.vue'
import DesignJobTimerToast from '@/components/design-jobs/DesignJobTimerToast.vue'
import TimezoneSelectionModal from '@/components/modals/TimezoneSelectionModal.vue'
import { toast } from '@/services/toastService'
import { usePermissionGuard } from '@/composables/usePermissionGuard'
import { useAuthStore } from '@/stores/auth'
import { userHasCompletedTwoFactorSetup } from '@/utils/userTwoFactor'
import { useAppReleaseWatcher } from '@/composables/useAppReleaseWatcher'
import { syncDocumentThemeFromStorage } from '@/utils/documentTheme'

const toastContainer = ref<InstanceType<typeof BaseToastContainer> | null>(null)
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// Use empty layout when user is forced to setup 2FA (no sidebar/links until 2FA is enabled)
const useSidebarLayout = computed(() => {
  if ((route.meta?.layout as string) !== 'sidebar') return false
  if (route.path === '/settings/two-factor') {
    if (!userHasCompletedTwoFactorSetup(authStore.user)) return false
    // During post-enable recovery modal, TwoFactorAuth sets hideAppSidebarForTwoFactorSettings so
    // refreshUser() cannot swap to AppLayoutWithSidebar and remount away the modal.
    if (authStore.hideAppSidebarForTwoFactorSettings) return false
  }
  return true
})

const impersonatedTargetName = computed(
  () =>
    authStore.impersonation.target?.name ??
    authStore.user?.name ??
    'User'
)

const isStoppingImpersonation = ref(false)

async function handleStopImpersonation() {
  if (isStoppingImpersonation.value) {
    return
  }

  isStoppingImpersonation.value = true
  authStore.impersonationTransitionInProgress = true
  try {
    await authStore.stopImpersonation()
    toast.success('Impersonation ended')
    await router.push({ name: 'Users' })
  } catch (error: unknown) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
      'Failed to stop impersonation'
    toast.error(message)
  } finally {
    authStore.impersonationTransitionInProgress = false
    isStoppingImpersonation.value = false
  }
}

// Router view key for forcing remounts when clicking active nav items
const routerViewKey = ref(0)

// Timezone modal state
const showTimezoneModal = ref(false)

// Provide a function to reload the current view
const reloadCurrentView = () => {
  routerViewKey.value++
}

// Provide this function so child components can trigger reloads
provide('reloadCurrentView', reloadCurrentView)

// Initialize permission guard to watch for permission changes
usePermissionGuard()

const { showUpdateBanner, reloadApp: reloadAppForUpdate } = useAppReleaseWatcher()

const hasTwoFactorEnabled = (): boolean => userHasCompletedTwoFactorSetup(authStore.user)

// Check if user needs to set timezone
const checkTimezoneRequirement = () => {
  // EARLY RETURN: If user already has timezone saved, never show modal
  if (authStore.user?.timezone) {
    return
  }
  
  // EARLY RETURN: Only show timezone modal if user has 2FA enabled
  if (!hasTwoFactorEnabled()) {
    return
  }
  
  if (authStore.isAuthenticated && !authStore.hasTimezone) {
    // Check if "Later" was clicked and 1 hour hasn't passed
    const laterUntil = sessionStorage.getItem('timezone_modal_later_until')
    if (laterUntil) {
      const laterTimestamp = parseInt(laterUntil, 10)
      const now = Date.now()
      if (now < laterTimestamp) {
        // Still within 1 hour window, don't show modal
        return
      }
      // 1 hour has passed, clear the flag and show modal
      sessionStorage.removeItem('timezone_modal_later_until')
    }
    // Show the modal
    showTimezoneModal.value = true
  }
}

// Watch for auth state changes
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  // Early return if user has timezone
  if (authStore.user?.timezone) {
    return
  }
  
  // Early return if user doesn't have 2FA enabled
  if (!hasTwoFactorEnabled()) {
    return
  }
  
  if (isAuthenticated) {
    // Wait for user data to be loaded from API
    setTimeout(() => {
      checkTimezoneRequirement()
    }, 1000)
  }
})

// Watch for user data changes (when refreshUser completes)
watch(() => authStore.user, (newUser) => {
  // Early return if user has timezone
  if (newUser?.timezone) {
    return
  }
  
  // Early return if user doesn't have 2FA enabled
  if (!hasTwoFactorEnabled()) {
    return
  }
  
  if (newUser && authStore.isAuthenticated) {
    // Small delay to ensure computed properties are updated
    setTimeout(() => {
      checkTimezoneRequirement()
    }, 100)
  }
}, { deep: true })

const handleTimezoneSaved = () => {
  // Clear the "Later" flag since timezone is now saved
  sessionStorage.removeItem('timezone_modal_later_until')
  // Modal will close automatically, just refresh user data
  authStore.refreshUser()
}

onMounted(async () => {
  // Initialize theme from localStorage or system preference (no-transitions still on from index.html)
  syncDocumentThemeFromStorage()

  // Re-enable transitions after first paint (prevents sidebar/layout collapse animation on reload)
  await nextTick()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions')
    })
  })

  // Register toast container
  await nextTick()
  if (toastContainer.value) {
    toast.registerContainer(toastContainer.value)
  }
  
  // Check timezone requirement after auth is initialized
  // Wait a bit for user data to be refreshed from API (router does this on initial load)
  if (authStore.isAuthenticated) {
    // Early return if user already has timezone
    if (!authStore.user?.timezone) {
      // Early return if user doesn't have 2FA enabled
      if (hasTwoFactorEnabled()) {
        // Give time for refreshUser() to complete (router calls it on initial load)
        setTimeout(() => {
          checkTimezoneRequirement()
        }, 1500)
      }
    }
  }
})
</script>